import { useState, useEffect } from 'react';
import axios from 'axios';

const API = 'https://weridesudan.com/api/admin';

export default function Settings({ token }: { token: string }) {
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    farePerKm: 10,
    surgeMultiplier: 1.0,
    iosVersion: '1.0.0',
    androidVersion: '1.0.0',
    notificationsEnabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/settings`, { headers })
      .then(r => setSettings(r.data))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    await axios.post(`${API}/settings`, settings, { headers });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (loading) return <div style={{ padding: 40, color: '#666' }}>Loading settings...</div>;

  return (
    <div style={{ padding: 24, maxWidth: 600 }}>
      <h2 style={{ marginBottom: 24 }}>⚙️ Settings</h2>

      {/* Maintenance Mode */}
      <Section title="🔧 Maintenance Mode" description="When ON, the app will show a maintenance message to all riders and drivers.">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Toggle
            value={settings.maintenanceMode}
            onChange={v => setSettings(s => ({ ...s, maintenanceMode: v }))}
          />
          <span style={{ color: settings.maintenanceMode ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
            {settings.maintenanceMode ? 'Maintenance ON' : 'App is Live'}
          </span>
        </div>
      </Section>

      {/* Fare Settings */}
      <Section title="💰 Fare Settings" description="Set the base fare and surge pricing multiplier.">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <label style={labelStyle}>Base Fare per KM (SDG)</label>
            <input
              type="number"
              value={settings.farePerKm}
              onChange={e => setSettings(s => ({ ...s, farePerKm: Number(e.target.value) }))}
              style={inputStyle}
              min={1}
            />
          </div>
          <div>
            <label style={labelStyle}>Surge Multiplier</label>
            <input
              type="number"
              value={settings.surgeMultiplier}
              onChange={e => setSettings(s => ({ ...s, surgeMultiplier: Number(e.target.value) }))}
              style={inputStyle}
              min={1} max={5} step={0.1}
            />
          </div>
        </div>
        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
          Estimated fare for 5km: {(settings.farePerKm * 5 * settings.surgeMultiplier).toFixed(0)} SDG
        </p>
      </Section>

      {/* App Versions */}
      <Section title="📱 App Version Info" description="Track the current live versions of your mobile apps.">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <label style={labelStyle}>iOS Version</label>
            <input
              value={settings.iosVersion}
              onChange={e => setSettings(s => ({ ...s, iosVersion: e.target.value }))}
              style={inputStyle}
              placeholder="1.0.0"
            />
          </div>
          <div>
            <label style={labelStyle}>Android Version</label>
            <input
              value={settings.androidVersion}
              onChange={e => setSettings(s => ({ ...s, androidVersion: e.target.value }))}
              style={inputStyle}
              placeholder="1.0.0"
            />
          </div>
        </div>
      </Section>

      {/* Notifications */}
      <Section title="🔔 Notifications" description="Enable or disable push notifications to drivers and riders.">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Toggle
            value={settings.notificationsEnabled}
            onChange={v => setSettings(s => ({ ...s, notificationsEnabled: v }))}
          />
          <span style={{ color: settings.notificationsEnabled ? '#22c55e' : '#9ca3af', fontWeight: 600 }}>
            {settings.notificationsEnabled ? 'Notifications Enabled' : 'Notifications Disabled'}
          </span>
        </div>
      </Section>

      {/* Save button */}
      <button
        onClick={save}
        disabled={saving}
        style={{ marginTop: 8, padding: '12px 32px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, cursor: 'pointer' }}
      >
        {saving ? 'Saving...' : '💾 Save Settings'}
      </button>

      {saved && (
        <span style={{ marginLeft: 16, color: '#22c55e', fontWeight: 600 }}>✅ Settings saved!</span>
      )}
    </div>
  );
}

function Section({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
      <h3 style={{ margin: '0 0 4px' }}>{title}</h3>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#9ca3af' }}>{description}</p>
      {children}
    </div>
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
        background: value ? '#1a73e8' : '#d1d5db',
        position: 'relative', transition: 'background 0.2s',
      }}
    >
      <div style={{
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        position: 'absolute', top: 3,
        left: value ? 24 : 4,
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, color: '#6b7280', marginBottom: 6 };
const inputStyle: React.CSSProperties = { padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, width: 160 };